import React, {
  Suspense,
  lazy,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {useHistory, useParams, useRouteMatch} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {getGuest} from "redux/actions/guestsActions";

import {InboxContext} from "routes/MessagesPage";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import Container from "connectui/component/layout/Container";
import Drawer from "connectui/component/modal/Drawer";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import Text from "connectui/component/display/Text";
import FilterSetDetailsPanel from "ui/component/inbox/panel/filtersets/FilterSetDetailsPanel";
import FlexContainer from "connectui/component/layout/FlexContainer";
import {setNavbarVisibility} from "redux/actions/settingsActions";
import {addBooking} from "redux/actions/bookingsActions";
// Panels
const FilterSetsPanel = lazy(
  () => import("ui/component/inbox/panel/filtersets/FilterSetsPanel"),
);
const MessageThreadsPanel = lazy(
  () => import("ui/component/inbox/panel/threads/MessageThreadsPanel"),
);
const ConversationsPanel = lazy(
  () => import("ui/component/inbox/panel/conversations/ConversationsPanel"),
);
const GuestDetailsPanel = lazy(
  () =>
    import("ui/component/inbox/panel/guestdetails/GuestDetailsPanelWrapper"),
);
const FiltersPanel = lazy(
  () => import("ui/component/inbox/panel/filterlist/FiltersPanel"),
);

const useStyles = (theme) =>
  buildStyles({
    outerContainer: {
      width: "100%",
      height: "100%",
      background: theme.colors.white,
      display: "flex",
      flexDirection: "column",
      overflow: "scroll",
      scrollbarWidth: "none",
    },
    iconBtn: {
      backgroundColor: theme.colors.white,
      color: theme.colors.textPrimary,
      cursor: "pointer",
      padding: "0px",
      border: "none",
      "&:hover": {backgroundColor: theme.colors.white},
    },
    menuIcon: {
      width: "20px",
      height: "21px",
      marginLeft: theme.spacing(0.5),
    },
    rightArrowBtn: {
      width: "25px",
      height: "25px",
      border: "none",
      padding: theme.spacing(2),
      backgroundColor: "transparent",
      "&:hover": {backgroundColor: "transparent"},
    },
  });

const MobileInbox = () => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();
  const chatMatch = useRouteMatch("/admin/messages/chat/:guest_id");
  const profileMatch = useRouteMatch("/admin/messages/profile/:guest_id");
  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const loading = useSelector((state) => state.defaultReducer.loading);
  const needsGuestLoad = useRef(false);
  const restoreBookings = useRef("initial");
  const loadingBookings = useRef("initial");
  const {setAppliedFilters} = useContext(InboxContext);
  // Panels: "guestlist", "conversations", "guestdetails", "filters", "editview"
  const [panelOpen, setPanelOpen] = useState("guestlist");
  // Views panels: "views"
  const [viewsOpen, setViewsOpen] = useState(false);
  const [selectedView, setSelectedView] = useState(null);
  const [guestBookings, setGuestBookings] = useState([]);
  const desktopGuestId = params.guest_id;
  const mobileGuestId =
    chatMatch?.params?.guest_id || profileMatch?.params?.guest_id;
  const guestId = !isMobile
    ? !["chat", "profile"].includes(desktopGuestId)
      ? desktopGuestId
      : null
    : mobileGuestId;

  useEffect(() => {
    if (panelOpen === "filters" || panelOpen === "editview") return;
    if (chatMatch?.params?.guest_id && panelOpen !== "conversations") {
      setPanelOpen("conversations");
    } else if (profileMatch?.params?.guest_id && panelOpen !== "guestdetails") {
      setPanelOpen("guestdetails");
    } else if (!chatMatch?.isExact && !profileMatch?.isExact) {
      setPanelOpen("guestlist");
    }
  }, [panelOpen, chatMatch, profileMatch]);

  useEffect(() => {
    dispatch(setNavbarVisibility(panelOpen === "guestlist"));
  }, [panelOpen]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const customFilters = searchParams.get("custom_filters");

    if (customFilters) {
      try {
        const parsedFilters = JSON.parse(decodeURIComponent(customFilters));
        setAppliedFilters(parsedFilters);
        window.history.pushState({}, "", window.location.pathname);
      } catch (error) {
        console.error("Failed to parse custom_filters from URL:", error);
      }
    }
  }, [setAppliedFilters]);

  useEffect(() => {
    if (restoreBookings.current === "restored") return;
    if (
      restoreBookings.current === "bookings_saved" &&
      loadingBookings.current === "bookings_loaded"
    ) {
      if (guestBookings.length) {
        dispatch(addBooking(guestBookings));
      }
      restoreBookings.current = "restored";
      return;
    }

    if (loading.bookings && loadingBookings.current === "initial") {
      loadingBookings.current = "in_progress";
    } else if (!loading.bookings && loadingBookings.current === "in_progress") {
      loadingBookings.current = "bookings_loaded";
    }
  }, [loading, guestBookings]);

  useEffect(() => {
    if (
      (chatMatch?.isExact || profileMatch?.isExact) &&
      guestId &&
      !guests[guestId]
    ) {
      if (needsGuestLoad.current !== guestId) {
        dispatch(getGuest(guestId, onGuestLoad));
        needsGuestLoad.current = guestId;
      }
    }
  }, [guests, guestId, chatMatch, profileMatch]);

  const sidebarIcon = (
    <ButtonIcon
      className={classes.iconBtn}
      icon={<SvgIcon component={ICONS.menu} className={classes.menuIcon} />}
      onClick={() => setViewsOpen(true)}
    />
  );

  const onGuestLoad = (guest, bookings) => {
    if (restoreBookings.current === "initial") {
      setGuestBookings(bookings);
      restoreBookings.current = "bookings_saved";
    }
  };

  const handleGuestClick = (guest) => {
    if (!guests[guest.guest_id]?.metadata) {
      dispatch(getGuest(guest.guest_id));
    }
    setPanelOpen("conversations");
  };

  const handleEditView = (filterSet) => {
    setViewsOpen(false);
    setSelectedView(filterSet);
    setPanelOpen("editview");
  };

  const handleSaveView = (filters) => {
    const newView = {
      color: "",
      icon: "",
      description: "",
      name: "",
      queries: filters,
    };
    setAppliedFilters(filters);
    setSelectedView(newView);
    setPanelOpen("editview");
  };

  const handleCloseView = () => {
    setPanelOpen("guestlist");
    setSelectedView(null);
  };

  return (
    <Container className={classes.outerContainer}>
      <Suspense fallback={<div></div>}>
        {/* Views */}
        <Drawer isOpen={viewsOpen} onClose={() => setViewsOpen(false)}>
          <FilterSetsPanel
            customHeader={
              <FlexContainer
                justifyContent="space-between"
                alignItems="center"
                sx={{width: "100%"}}
              >
                <Text variant="displayXS" weight="semibold">
                  {"Views"}
                </Text>
                <ButtonIcon
                  className={classes.rightArrowBtn}
                  icon={<SvgIcon component={ICONS["chevron-right"]} />}
                  onClick={() => setViewsOpen(false)}
                />
              </FlexContainer>
            }
            onClick={() => setViewsOpen(false)}
            editView={handleEditView}
          />
        </Drawer>

        {/* Main panels */}
        {panelOpen === "guestlist" && (
          <MessageThreadsPanel
            onGuestClick={handleGuestClick}
            openFilters={() => setPanelOpen("filters")}
            expandIcon={sidebarIcon}
          />
        )}
        {panelOpen === "conversations" && (
          <ConversationsPanel
            guestId={guestId}
            goBack={() => {
              history.replace("/admin/messages");
            }}
            togglePanelOpen={() => {
              history.push(`/admin/messages/profile/${guestId}`);
            }}
          />
        )}
        {panelOpen === "guestdetails" && <GuestDetailsPanel />}
        {/* Sub-panels */}
        {panelOpen === "filters" && (
          <FiltersPanel
            onClose={() => setPanelOpen("guestlist")}
            saveView={handleSaveView}
          />
        )}
        {panelOpen === "editview" && (
          <FilterSetDetailsPanel
            view={selectedView}
            onClose={handleCloseView}
          />
        )}
      </Suspense>
    </Container>
  );
};

export default MobileInbox;
